// Utilidades compartidas para exportar reportes a Excel con formato real
// (negrita, colores, bordes, panel congelado) usando exceljs — a diferencia
// de `xlsx` (SheetJS community), exceljs SÍ persiste estilos al escribir.
import ExcelJS from "exceljs";

export const BRAND_COLOR = "FF1F4E78"; // azul oscuro del header
export const BRAND_COLOR_LIGHT = "FFDCE6F1";
const BAND_COLOR = "FFF4F7FC";
const BORDER_COLOR = "FFD9D9D9";

const THIN_BORDER = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

// Colores de fondo por estado, coherentes con los Tag de la UI
const STATUS_FILL = [
  { match: /cancelado/i, argb: "FFFDE3E3", font: "FFA8071A" },
  { match: /finalizado|entregado/i, argb: "FFE6F7E0", font: "FF237804" },
  { match: /pendiente de pago|pendiente pago/i, argb: "FFFFF1E6", font: "FFAD4E00" },
  { match: /pendiente/i, argb: "FFFFFBE0", font: "FF876800" },
  { match: /traspaso|espera|recepcionando|picking/i, argb: "FFFFF3D6", font: "FFAD6800" },
  { match: /procesando/i, argb: "FFE6F4FF", font: "FF0958A6" },
  { match: /preparando/i, argb: "FFEFF1FF", font: "FF2846A6" },
  { match: /listo para/i, argb: "FFE0F7F7", font: "FF08616B" },
  { match: /programada/i, argb: "FFF4E8FF", font: "FF5B1E9E" },
];

const statusFillFor = (text) => {
  const s = String(text || "");
  return STATUS_FILL.find((c) => c.match.test(s)) || null;
};

const isEmptyCell = (v) => v === undefined || v === null;

const autoWidth = (headers, rows, keys) =>
  keys.map((k, i) => {
    const headerLen = headers[i].length;
    const maxContentLen = rows.reduce((max, row) => {
      const v = row[k];
      return Math.max(max, isEmptyCell(v) ? 0 : String(v).length);
    }, 0);
    return Math.min(Math.max(headerLen, maxContentLen, 8) + 2, 45);
  });

// ---------------------------------------------------------------------------
// Hoja de resumen ejecutivo: título, período, tabla de KPIs.
// ---------------------------------------------------------------------------
export function addSummarySheet(workbook, { title, generatedAt, period, kpis }) {
  const ws = workbook.addWorksheet("Resumen", { views: [{ showGridLines: false }] });
  ws.columns = [{ width: 28 }, { width: 22 }];

  ws.mergeCells("A1:B1");
  const titleCell = ws.getCell("A1");
  titleCell.value = title;
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_COLOR } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 28;

  ws.addRow(["Generado el", generatedAt]);
  ws.addRow(["Periodo", period]);
  ws.addRow([]);

  const headerRowIdx = ws.rowCount + 1;
  ws.addRow(["Indicador", "Valor"]);
  const headerRow = ws.getRow(headerRowIdx);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_COLOR } };
    cell.border = THIN_BORDER;
  });

  kpis.forEach(([label, value], idx) => {
    const row = ws.addRow([label, value]);
    if (idx % 2 === 1) {
      row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BAND_COLOR } };
      });
    }
    row.eachCell((cell) => {
      cell.border = THIN_BORDER;
    });
    row.getCell(1).font = { bold: true };
  });

  return ws;
}

// ---------------------------------------------------------------------------
// Hoja de datos (Pedidos / Detalle SKUs): header con color, bordes, bandas
// alternadas, formato de moneda, columna de estado resaltada, autofiltro y
// panel congelado en la primera fila.
// ---------------------------------------------------------------------------
export function addDataSheet(workbook, { name, rows, cols, moneyCols, statusKey }) {
  const headers = cols.map((c) => c.title);
  const keys = cols.map((c) => c.value);
  const widths = autoWidth(headers, rows, keys);

  const ws = workbook.addWorksheet(name, { views: [{ state: "frozen", ySplit: 1 }] });
  ws.columns = keys.map((k, i) => ({ header: headers[i], key: k, width: widths[i] }));

  const headerRow = ws.getRow(1);
  headerRow.height = 20;
  keys.forEach((k, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: (cols[i] && cols[i].headerFill) || BRAND_COLOR },
    };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = THIN_BORDER;
  });

  rows.forEach((row, idx) => {
    const values = {};
    keys.forEach((k) => { values[k] = isEmptyCell(row[k]) ? "" : row[k]; });
    const excelRow = ws.addRow(values);

    if (idx % 2 === 1) {
      excelRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BAND_COLOR } };
      });
    }
    excelRow.eachCell((cell) => {
      cell.border = THIN_BORDER;
    });

    keys.forEach((k, colIdx) => {
      if (moneyCols.indexOf(k) >= 0 && typeof values[k] === "number") {
        excelRow.getCell(colIdx + 1).numFmt = "#,##0.00";
        excelRow.getCell(colIdx + 1).alignment = { horizontal: "right" };
      }
    });

    if (statusKey) {
      const statusColIdx = keys.indexOf(statusKey);
      if (statusColIdx >= 0) {
        const fill = statusFillFor(values[statusKey]);
        if (fill) {
          const cell = excelRow.getCell(statusColIdx + 1);
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill.argb } };
          cell.font = { color: { argb: fill.font }, bold: true };
        }
      }
    }
  });

  if (rows.length > 0) {
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: keys.length } };
  }

  return ws;
}

// ---------------------------------------------------------------------------
// Dispara la descarga del workbook en el navegador (sin dependencias extra).
// ---------------------------------------------------------------------------
export async function downloadWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export { ExcelJS };
